import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChefHat, Plus, X, Clock, Users, Star, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIRecipeGenerator = ({ token }) => {
  const { t } = useTranslation();
  const [recipeRequest, setRecipeRequest] = useState({
    ingredients: [],
    dietary_restrictions: [],
    cuisine_type: 'any',
    serving_size: 4,
    cooking_time: 'medium'
  });
  const [generatedRecipes, setGeneratedRecipes] = useState([]);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('create');
  const [ingredientInput, setIngredientInput] = useState('');

  useEffect(() => {
    fetchGeneratedRecipes();
  }, []);

  const fetchGeneratedRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes/generated`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedRecipes(response.data.recipes || []);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const addIngredient = () => {
    if (ingredientInput.trim() && !recipeRequest.ingredients.includes(ingredientInput.trim())) {
      setRecipeRequest({
        ...recipeRequest,
        ingredients: [...recipeRequest.ingredients, ingredientInput.trim()]
      });
      setIngredientInput('');
    }
  };

  const removeIngredient = (ingredient) => {
    setRecipeRequest({
      ...recipeRequest,
      ingredients: recipeRequest.ingredients.filter(item => item !== ingredient)
    });
  };

  const generateRecipe = async () => {
    if (recipeRequest.ingredients.length === 0) {
      alert('Please add at least one ingredient!');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/recipes/generate`, recipeRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentRecipe(response.data.recipe);
      setActiveView('view');
      fetchGeneratedRecipes();
      alert('🎉 AI Recipe generated successfully!');
    } catch (error) {
      console.error('Error generating recipe:', error);
      alert(error.response?.data?.detail || 'Failed to generate recipe');
    } finally {
      setLoading(false);
    }
  };

  const dietaryOptions = [
    'Vegetarian', 'Vegan', 'Keto', 'Paleo', 'Gluten-Free', 
    'Dairy-Free', 'Nut-Free', 'Low-Carb', 'High-Protein'
  ];

  const cuisineOptions = [
    'any', 'Italian', 'Asian', 'Mexican', 'Mediterranean', 'Indian', 
    'American', 'Thai', 'Japanese', 'French', 'Middle Eastern'
  ];

  const toggleDietaryRestriction = (restriction) => {
    const current = recipeRequest.dietary_restrictions;
    if (current.includes(restriction)) {
      setRecipeRequest({
        ...recipeRequest,
        dietary_restrictions: current.filter(item => item !== restriction)
      });
    } else {
      setRecipeRequest({
        ...recipeRequest,
        dietary_restrictions: [...current, restriction]
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addIngredient();
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-recipe-generator">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
          <ChefHat size={24} className="text-[#FF6600]" />
          AI Recipe Generator
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Transform your ingredients into delicious recipes with AI
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveView('create')}
          className={`px-4 py-2 text-sm rounded-lg ${activeView === 'create' ? 'bg-[#FF6600] text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
          data-testid="tab-generate"
        >
          <Plus className="inline mr-2" size={16} />
          Generate Recipe
        </button>
        <button
          onClick={() => setActiveView('history')}
          className={`px-4 py-2 text-sm rounded-lg ${activeView === 'history' ? 'bg-[#FF6600] text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
          data-testid="tab-history"
        >
          <BookOpen className="inline mr-2" size={16} />
          My Recipes ({generatedRecipes.length})
        </button>
      </div>

      {/* Create Recipe View */}
      {activeView === 'create' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">Generate Recipe from Ingredients</h3>
          
          <div className="space-y-4">
            {/* Ingredients Input */}
            <div>
              <label className="text-sm font-semibold block mb-2">Your Ingredients</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={ingredientInput}
                  onChange={(e) => setIngredientInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter an ingredient (e.g., chicken, tomatoes, garlic)"
                  className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                  data-testid="ingredient-input"
                />
                <button
                  onClick={addIngredient}
                  className="px-4 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00]"
                  data-testid="add-ingredient-btn"
                >
                  <Plus size={20} />
                </button>
              </div>
              
              {/* Ingredient Tags */}
              {recipeRequest.ingredients.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {recipeRequest.ingredients.map((ingredient, idx) => (
                    <div key={idx} className="bg-orange-100 text-[#FF6600] px-3 py-1.5 rounded-lg flex items-center gap-2">
                      <span className="text-sm font-medium">{ingredient}</span>
                      <button
                        onClick={() => removeIngredient(ingredient)}
                        className="text-[#FF6600] hover:text-red-500"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cuisine Type */}
              <div>
                <label className="text-sm font-semibold block mb-2">Cuisine Type</label>
                <select
                  value={recipeRequest.cuisine_type}
                  onChange={(e) => setRecipeRequest({...recipeRequest, cuisine_type: e.target.value})}
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                >
                  {cuisineOptions.map((cuisine) => (
                    <option key={cuisine} value={cuisine}>
                      {cuisine.charAt(0).toUpperCase() + cuisine.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Serving Size */}
              <div>
                <label className="text-sm font-semibold block mb-2">Serving Size</label>
                <div className="flex items-center gap-2">
                  <Users size={20} className="text-gray-400" />
                  <select
                    value={recipeRequest.serving_size}
                    onChange={(e) => setRecipeRequest({...recipeRequest, serving_size: parseInt(e.target.value)})}
                    className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                  >
                    {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((size) => (
                      <option key={size} value={size}>
                        {size} {size === 1 ? 'person' : 'people'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cooking Time Preference */}
              <div>
                <label className="text-sm font-semibold block mb-2">Cooking Time</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{key: 'quick', label: 'Quick (<30m)'}, {key: 'medium', label: 'Medium (30-60m)'}, {key: 'elaborate', label: 'Elaborate (60m+)'}].map((option) => (
                    <button
                      key={option.key}
                      onClick={() => setRecipeRequest({...recipeRequest, cooking_time: option.key})}
                      className={`p-2 rounded-lg text-center text-xs ${recipeRequest.cooking_time === option.key ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
                    >
                      <Clock size={14} className="inline mr-1" />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Dietary Restrictions */}
            <div>
              <label className="text-sm font-semibold block mb-2">Dietary Restrictions (optional)</label>
              <div className="flex flex-wrap gap-2">
                {dietaryOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => toggleDietaryRestriction(option)}
                    className={`px-3 py-1.5 rounded-lg text-xs ${recipeRequest.dietary_restrictions.includes(option) ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={generateRecipe}
              disabled={loading || recipeRequest.ingredients.length === 0}
              className="w-full py-3 bg-[#FF6600] text-white font-semibold rounded-lg hover:bg-[#E55C00] disabled:opacity-50"
              data-testid="generate-recipe-btn"
            >
              {loading ? '🤖 Generating AI Recipe...' : '✨ Generate AI Recipe'}
            </button>
          </div>
        </div>
      )}

      {/* Recipe History */}
      {activeView === 'history' && (
        <div className="space-y-4">
          {generatedRecipes.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm text-center">
              <ChefHat size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="font-bold mb-2">No recipes generated yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Create your first AI recipe to get started!</p>
              <button onClick={() => setActiveView('create')} className="px-4 py-2 bg-[#FF6600] text-white rounded-lg font-semibold">
                Generate First Recipe
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {generatedRecipes.map((recipe, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm">{recipe.name}</h4>
                    <Star size={16} className="text-[#FF6600]" />
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {recipe.ingredients?.slice(0, 3).join(', ')}
                    {recipe.ingredients?.length > 3 && '...'}
                  </p>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-300 mb-3">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {recipe.cooking_time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={12} />
                      {recipe.serving_size}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => { setCurrentRecipe(recipe); setActiveView('view'); }}
                    className="w-full py-2 border border-[#FF6600] text-[#FF6600] rounded-lg text-sm font-semibold hover:bg-orange-50"
                  >
                    View Recipe
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recipe Viewer */}
      {activeView === 'view' && currentRecipe && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold mb-1">{currentRecipe.name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">{currentRecipe.description}</p>
            </div>
            <button onClick={() => setActiveView('history')} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:bg-gray-900">
              ← Back to Recipes
            </button>
          </div>

          {currentRecipe.recipe_data ? (
            <div className="space-y-4">
              {/* Recipe Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
                <div className="text-center">
                  <Clock size={20} className="mx-auto mb-1 text-[#FF6600]" />
                  <div className="text-sm font-semibold">{currentRecipe.recipe_data.total_time_minutes || 'N/A'} min</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total Time</div>
                </div>
                <div className="text-center">
                  <Users size={20} className="mx-auto mb-1 text-[#FF6600]" />
                  <div className="text-sm font-semibold">{currentRecipe.serving_size}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Servings</div>
                </div>
                <div className="text-center">
                  <span className="text-lg mb-1 block">🔥</span>
                  <div className="text-sm font-semibold">{currentRecipe.recipe_data.nutrition?.calories_per_serving || 'N/A'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Calories</div>
                </div>
                <div className="text-center">
                  <Star size={20} className="mx-auto mb-1 text-[#FF6600]" />
                  <div className="text-sm font-semibold capitalize">{currentRecipe.recipe_data.difficulty || 'Medium'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Difficulty</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Ingredients */}
                <div>
                  <h4 className="font-bold mb-3">Ingredients</h4>
                  <div className="space-y-2">
                    {currentRecipe.recipe_data.ingredients?.map((ingredient, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded">
                        <input type="checkbox" className="w-4 h-4" />
                        <span className="text-sm">
                          {typeof ingredient === 'object' 
                            ? `${ingredient.amount || ''} ${ingredient.unit || ''} ${ingredient.item || ingredient}`.trim()
                            : ingredient
                          }
                        </span>
                      </div>
                    )) || <p className="text-gray-500 dark:text-gray-400 text-sm">No ingredients listed</p>}
                  </div>
                </div>

                {/* Instructions */}
                <div>
                  <h4 className="font-bold mb-3">Instructions</h4>
                  <div className="space-y-3">
                    {currentRecipe.recipe_data.instructions?.map((step, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="w-6 h-6 bg-[#FF6600] text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                          {idx + 1}
                        </div>
                        <p className="text-sm flex-1 pt-1">{step}</p>
                      </div>
                    )) || <p className="text-gray-500 dark:text-gray-400 text-sm">No instructions available</p>}
                  </div>
                </div>
              </div>

              {/* Tips & Variations */}
              {(currentRecipe.recipe_data.tips || currentRecipe.recipe_data.variations) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentRecipe.recipe_data.tips && (
                    <div>
                      <h4 className="font-bold mb-3">💡 Cooking Tips</h4>
                      <ul className="space-y-1">
                        {currentRecipe.recipe_data.tips.map((tip, idx) => (
                          <li key={idx} className="text-sm">• {tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {currentRecipe.recipe_data.variations && (
                    <div>
                      <h4 className="font-bold mb-3">🔄 Variations</h4>
                      <ul className="space-y-1">
                        {currentRecipe.recipe_data.variations.map((variation, idx) => (
                          <li key={idx} className="text-sm">• {variation}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Nutrition */}
              {currentRecipe.recipe_data.nutrition && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-bold mb-3">Nutrition Per Serving</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div>
                      <div className="font-semibold text-[#FF6600]">{Math.round(currentRecipe.recipe_data.nutrition.calories_per_serving || 0)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Calories</div>
                    </div>
                    <div>
                      <div className="font-semibold">{Math.round(currentRecipe.recipe_data.nutrition.protein || 0)}g</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Protein</div>
                    </div>
                    <div>
                      <div className="font-semibold">{Math.round(currentRecipe.recipe_data.nutrition.carbs || 0)}g</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Carbs</div>
                    </div>
                    <div>
                      <div className="font-semibold">{Math.round(currentRecipe.recipe_data.nutrition.fats || 0)}g</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Fats</div>
                    </div>
                    <div>
                      <div className="font-semibold">{Math.round(currentRecipe.recipe_data.nutrition.fiber || 0)}g</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Fiber</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">Recipe data not available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AIRecipeGenerator;
